import axios from 'axios';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import api from '../services/api';

interface AuthState {
  token: string;
  refresh_token: string;
  companyName: string;
  userName: string;
  companyId: string;
}

interface SignInCredentials {
  user: string;
  password: string;
  captcha: string | null | undefined;
}

interface IErrorResponse {
  error: boolean;
  message: string;
}

interface IResponseRefreshToken {
  result: string;
  token: string;
  refresh_token: string;
  message: string;
  version: string;
}

interface AuthContextData {
  userName: string;
  companyName: string;
  signIn(credentials: SignInCredentials): Promise<IErrorResponse>;
  signOut(): void;
  apiVersion: string;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const history = useHistory();

  const [apiVersion, setApiVersion] = useState('');
  const [data, setData] = useState<AuthState>(() => {
    const token = sessionStorage.getItem('@SWINTbalcao:token');
    const refresh_token = sessionStorage.getItem('@SWINTbalcao:refresh_token');
    const companyName = localStorage.getItem('@SWINTbalcao:companyName');
    const userName = localStorage.getItem('@SWINTbalcao:userName');
    const companyId = localStorage.getItem('@SWINTbalcao:companyId');
    const apiV = localStorage.getItem('@SWINTbalcao:apiVersion');

    if (
      token &&
      companyName &&
      userName &&
      companyId &&
      refresh_token &&
      apiV
    ) {
      api.defaults.headers.authorization = `Bearer ${token}`;

      setApiVersion(apiV);

      return {
        token,
        refresh_token,
        companyName,
        userName,
        companyId,
      };
    }

    return {} as AuthState;
  });

  const LogIn = useCallback((token, refresh_token, apiV) => {
    sessionStorage.setItem('@SWINTbalcao:token', token);
    sessionStorage.setItem('@SWINTbalcao:refresh_token', refresh_token);
    localStorage.setItem('@SWINTbalcao:token', token);
    localStorage.setItem('@SWINTbalcao:refresh_token', refresh_token);
    localStorage.setItem('@SWINTbalcao:apiVersion', apiV);
  }, []);

  const refreshToken = useCallback(async (): Promise<{
    data: IResponseRefreshToken;
    status: number;
  }> => {
    const refresh = sessionStorage.getItem('@SWINTbalcao:refresh_token');

    const res = (await api
      .post('/session/refresh-token', {
        token: refresh,
      })
      .then(response => response)
      .catch(err => err.response)) as {
      data: IResponseRefreshToken;
      status: number;
    };

    const { token, refresh_token, result, version } = res.data;

    if (result === 'error') {
      signOut();
      history.push('/');
      window.location.reload();
      return res;
    }

    setApiVersion(version || '1.0.0');
    LogIn(token, refresh_token, version || '1.0.0');
    return res;
  }, [LogIn]);

  api.interceptors.response.use(
    response => response,
    async ({ response, config }) => {
      const originalReq = config;

      if (response.status === 401) {
        originalReq._retry = true;
        const responseRefreshToken = await refreshToken();

        if (
          responseRefreshToken?.status === 200 &&
          responseRefreshToken?.data.token
        ) {
          originalReq.headers.authorization = `Bearer ${responseRefreshToken.data.token}`;
          api.defaults.headers.authorization = `Bearer ${responseRefreshToken.data.token}`;
          return axios(originalReq);
        }
      }
      return false;
    },
  );

  const signOut = useCallback(() => {
    localStorage.removeItem('@SWINTbalcao:token');
    localStorage.removeItem('@SWINTbalcao:refresh_token');
    localStorage.removeItem('@SWINTbalcao:companyName');
    localStorage.removeItem('@SWINTbalcao:companyId');
    localStorage.removeItem('@SWINTbalcao:userName');
    sessionStorage.removeItem('@SWINTbalcao:token');
    sessionStorage.removeItem('@SWINTbalcao:refresh_token');

    setData({} as AuthState);
  }, []);

  const signIn = useCallback(
    async (loginData: SignInCredentials): Promise<IErrorResponse> => {
      api.defaults.headers.creuser = loginData.user;

      const res = await api
        .post('session/create', loginData)
        .then(response => response)
        .catch(err => err.response);

      if (!res) return { error: true, message: 'Erro ao realizar o login' };

      const {
        token,
        refresh_token,
        companyName,
        companyId,
        userName,
        error,
        result,
        message,
        version,
      } = res.data;

      if (error) return { error: true, message: error.details[0].message };

      if (result === 'error' || result === 'warn')
        return { error: true, message };

      localStorage.setItem('@SWINTbalcao:token', token);
      localStorage.setItem('@SWINTbalcao:refresh_token', refresh_token);
      localStorage.setItem('@SWINTbalcao:companyName', companyName);
      localStorage.setItem('@SWINTbalcao:companyId', companyId);
      localStorage.setItem('@SWINTbalcao:userName', userName);
      localStorage.setItem('@SWINTPlataforma:apiVersion', version || '1.0.0');
      sessionStorage.setItem('@SWINTbalcao:token', token);
      sessionStorage.setItem('@SWINTbalcao:refresh_token', refresh_token);

      api.defaults.headers.authorization = `Bearer ${token}`;

      setData({ token, companyName, userName, companyId, refresh_token });
      setApiVersion(version || '1.0.0');

      return { error: false, message: 'success' };
    },
    [],
  );

  useEffect(() => {
    const session = localStorage.getItem('@SWINTbalcao:token');

    if (!session) {
      signOut();
    }
  }, [signOut]);

  return (
    <AuthContext.Provider
      value={{
        companyName: data.companyName,
        userName: data.userName,
        signIn,
        signOut,
        apiVersion,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
